/**
 * The parts of the Z80 programming model that more than one module has to agree on: the register
 * list the emulator shows, the flag bits, and the console device that programs talk to through
 * `in`/`out`. The emulator adapter, the editor tooling and the documentation pages all read from
 * here so that a port number or a register name is defined exactly once.
 *
 * This module is plain data: no Svelte runes, no imports from the rest of the app, so it can be
 * imported from a `+page.server.ts` during prerendering and probed from node.
 */

/**
 * Registers shown in the register panel and accepted by testcases, in display order. `a` is an
 * 8 bit register, everything else is a 16 bit pair. The other 8 bit halves (b, c, d, e, h, l, f)
 * are visible as the byte groups of their pair, the flags register is rendered as the status
 * flags section (see `Z80_FLAGS`). The alternate set (`af'`, `bc'`, `de'`, `hl'`) is only reachable
 * through `ex af,af'` and `exx`; `i`, `r` and the undocumented `ixh`/`ixl`/`iyh`/`iyl` halves are
 * not shown.
 */
export const Z80_REGISTER_NAMES = [
    'a',
    'bc',
    'de',
    'hl',
    'ix',
    'iy',
    'sp',
    'pc',
    "af'",
    "bc'",
    "de'",
    "hl'"
] as const

export type Z80RegisterName = (typeof Z80_REGISTER_NAMES)[number]

/**
 * The registers a testcase may preset. `pc` is excluded: loading the assembled program decides
 * where execution starts (the `end` directive's address, else the first address with code).
 */
export const Z80_STARTING_REGISTER_NAMES: Z80RegisterName[] = Z80_REGISTER_NAMES.filter(
    (name) => name !== 'pc'
)

/**
 * The documented bits of the F register, in the order the status flags section shows them
 * (most significant first). Bits 3 and 5 are undocumented copies of the result and are not shown.
 */
export const Z80_FLAGS = [
    {
        name: 'S',
        bit: 7,
        description: 'Sign: set when the result is negative (bit 7 of the result).'
    },
    { name: 'Z', bit: 6, description: 'Zero: set when the result is zero.' },
    {
        name: 'H',
        bit: 4,
        description: 'Half carry: carry from bit 3 to bit 4, used by `daa` for BCD arithmetic.'
    },
    {
        name: 'P/V',
        bit: 2,
        description:
            'Parity/overflow: parity of the result for logical and rotate instructions (set when even), signed overflow for arithmetic instructions.'
    },
    {
        name: 'N',
        bit: 1,
        description: 'Add/subtract: set when the last operation was a subtraction, used by `daa`.'
    },
    {
        name: 'C',
        bit: 0,
        description:
            'Carry: carry out of bit 7 (or 15), borrow for subtractions, and the bit shifted out by rotates and shifts.'
    }
] as const

/**
 * The 6 character flag strings in the instruction table (`ClrInstruction.flags` from `@specy/z80`)
 * list the flags in this order: `add a,b` is `+0V+++` (C affected, N reset, P/V holds overflow, H,
 * Z and S affected), `cpl` is `-1-1--` (N and H set, the others untouched).
 */
export const Z80_FLAG_STRING_ORDER = ['C', 'N', 'P/V', 'H', 'Z', 'S'] as const

/**
 * Meaning of one character of a flag string.
 */
export const Z80_FLAG_STRING_LEGEND: Record<string, string> = {
    '-': 'not affected',
    '+': 'affected by the result',
    '0': 'reset',
    '1': 'set',
    '*': 'undefined',
    V: 'holds the signed overflow',
    P: 'holds the parity of the result',
    ' ': 'undefined'
}

/**
 * Where the default program is assembled (`.org 0x8000`) and where the global memory tab opens.
 * The low 64 bytes of a real Z80 hold the reset and `rst` vectors, so programs traditionally live
 * higher up.
 */
export const Z80_DEFAULT_ORG = 0x8000

/**
 * Initial stack pointer. The Z80 decrements SP before writing, so the first push lands at
 * 0xFFFD-0xFFFE and the top of memory is never written by the stack itself.
 */
export const Z80_STACK_TOP = 0xffff

export const Z80_MEMORY_SIZE = 0x10000

/**
 * The largest Screen a Z80 program can ask for. Coordinates and sizes are single bytes, so the
 * Screen is at most 256 by 256 logical pixels; a size byte of 0 means 256, the only size that does
 * not fit in a byte ([ADR 0011](../../../../../../docs/adr/0011-z80-peripherals-through-the-port-map.md)).
 */
export const Z80_SCREEN_MAX_SIZE = 256

/**
 * The port map: how a Z80 program reaches every peripheral. A Z80 has no system calls, so programs
 * talk to the outside world with `in` and `out`, which address one of 256 ports with the low byte
 * of the address bus. The emulator decodes four groups — the console ports of
 * [ADR 0002](../../../../../../docs/adr/0002-z80-console-ports.md) for the Terminal, and the Screen,
 * Keyboard, Mouse and program-time ports of
 * [ADR 0011](../../../../../../docs/adr/0011-z80-peripherals-through-the-port-map.md) — and every other
 * port behaves like an empty bus: writes are dropped and reads answer 0xFF.
 *
 * `out (n),a` and `in a,(n)` put A on the high byte of the address bus; the `(c)` forms
 * (`out (c),r`, `in r,(c)`) put B there. B is how a read carries its parameter: the key code for
 * the key-state port, the view for the mouse ports, the byte index for the time port and the
 * duration for the wait port. Of the writes, only the WORD console port reads the high byte.
 */
export const Z80_PORTS = {
    /**
     * Character port. Writing sends the byte to the terminal as a character (Latin-1: 0x41 prints
     * "A", 0x0A is a newline) and draws it at the Screen's text cursor. Reading returns the next
     * character of the current input line; when no input is buffered the program pauses until a
     * line has been typed (or, during testcases, taken from the testcase input). The line is
     * delivered with a trailing newline (0x0A) so a program can read until it sees one. Once the
     * program has used a Screen, Keyboard or Mouse port the characters come from the Screen's
     * Keyboard instead, one keystroke at a time. Reads never fail: end of input only pauses the
     * program.
     */
    CHAR: 0x10,
    /**
     * Number port. Writing prints the byte as an unsigned decimal number (0 to 255). Reading asks
     * for a whole line, parses it as a decimal number (a leading minus sign is accepted) and
     * returns its low byte; a line that is not a number stops the program with an error.
     */
    NUMBER: 0x11,
    /**
     * Signed number port. Writing prints the byte as a signed decimal number (-128 to 127).
     * Reading behaves like the `NUMBER` port.
     */
    SIGNED: 0x12,
    /**
     * Hexadecimal port. Writing prints the byte as two upper case hexadecimal digits (no prefix).
     * Reading asks for a line, parses it as hexadecimal (an optional `0x`, `$` prefix or `h`
     * suffix is accepted) and returns its low byte; an invalid line stops the program with an error.
     */
    HEX: 0x13,
    /**
     * 16 bit number port. Writing prints the unsigned decimal value of the 16 bit number whose
     * high byte is the high byte of the port address and whose low byte is the byte written. With
     * `ld b,h` / `ld c,0x14` / `out (c),l` that prints HL. Reading behaves like the `NUMBER` port
     * (only the low byte can be returned).
     */
    WORD: 0x14,

    /** Pen color, one 3-3-2 byte: the color of lines, outlines, pixels and text. */
    SCREEN_PEN_COLOR: 0x20,
    /** Fill color, one 3-3-2 byte: the inside of filled shapes, the flood fill and the clear. */
    SCREEN_FILL_COLOR: 0x21,
    /** Pen width in pixels, 1 or more, for lines and outlines. */
    SCREEN_PEN_WIDTH: 0x22,
    /** First X coordinate: the point of a pixel, the start of a line, a corner of a shape. */
    SCREEN_X: 0x23,
    /** First Y coordinate. */
    SCREEN_Y: 0x24,
    /** Second X coordinate: the end of a line or the opposite corner of a shape. */
    SCREEN_X2: 0x25,
    /** Second Y coordinate. */
    SCREEN_Y2: 0x26,
    /** Runs one drawing operation on the coordinates and colors already set; see `Z80_SCREEN_COMMANDS`. */
    SCREEN_COMMAND: 0x27,
    /** Reads the color of the pixel at (X, Y) as a 3-3-2 byte. */
    SCREEN_PIXEL: 0x28,
    /** Text cursor column, in 8 by 8 character cells: 0 to 31 on the default Screen. */
    SCREEN_CURSOR_COLUMN: 0x29,
    /** Text cursor row, in 8 by 8 character cells: 0 to 23 on the default Screen. */
    SCREEN_CURSOR_ROW: 0x2a,

    /** 1 when a character typed on the Screen (or a testcase input line) is waiting, else 0. */
    KEY_AVAILABLE: 0x30,
    /** 1 while the key whose code is in B is held down, else 0. */
    KEY_STATE: 0x31,
    /** The code of the last key pressed, 0 before the first press. */
    KEY_LAST_DOWN: 0x32,
    /** The code of the last key released, 0 before the first release. */
    KEY_LAST_UP: 0x33,

    /** Mouse X in logical Screen pixels; B selects the view, see `Z80_MOUSE_VIEWS`. */
    MOUSE_X: 0x40,
    /** Mouse Y in logical Screen pixels; B selects the view. */
    MOUSE_Y: 0x41,
    /** Buttons and modifiers of the selected view, see `Z80_MOUSE_FLAGS`. */
    MOUSE_BUTTONS: 0x42,
    /** The mouse event counter, a byte that wraps: how a program tells a new event from an old one. */
    MOUSE_EVENTS: 0x43,

    /** Waits B hundredths of a second and answers 0; the GUI and Stop stay responsive meanwhile. */
    TIME_WAIT: 0x50,
    /** Waits for the next animation frame and answers 0: the pacing of an animated program. */
    TIME_FRAME: 0x51,
    /** One byte of the hundredths of a second since the run started; B selects the byte, 0 lowest. */
    TIME_NOW: 0x52,

    /** Plays a Note at the written pitch with the instrument, volume and duration set, and goes on. */
    NOTE_PLAY: 0x60,
    /** Plays a Note at the pitch in B, waits its duration and answers 0. */
    NOTE_PLAY_WAIT: 0x61,
    /** The General MIDI instrument of the next Notes, 0 to 127. */
    NOTE_INSTRUMENT: 0x62,
    /** The volume of the next Notes, 0 to 127. */
    NOTE_VOLUME: 0x63,
    /** The duration of the next Notes in milliseconds: B the high byte, the written value the low. */
    NOTE_DURATION: 0x64,

    /** Selects which of the Sound chip's 16 registers the next write or read reaches, as on the MSX. */
    SOUND_CHIP_SELECT: 0xa0,
    /** Writes the selected Sound chip register. */
    SOUND_CHIP_WRITE: 0xa1,
    /** Reads the selected Sound chip register back. */
    SOUND_CHIP_READ: 0xa2
} as const

export type Z80PortName = keyof typeof Z80_PORTS

/** The peripheral a port belongs to. The documentation page and the agent prompt group by it. */
export type Z80PortGroup =
    | 'console'
    | 'screen'
    | 'keyboard'
    | 'mouse'
    | 'time'
    | 'audio'
    | 'sound-chip'

/**
 * The groups of [ADR 0011](../../../../../../docs/adr/0011-z80-peripherals-through-the-port-map.md),
 * the audio and Sound chip ones of its 2026-10-23 updates included, plus the console ports they
 * extend, in the order the documentation shows them.
 */
export const Z80_PORT_GROUP_DOCS: {
    group: Z80PortGroup
    title: string
    range: string
    description: string
}[] = [
    {
        group: 'console',
        title: 'Console',
        range: '0x10 - 0x14',
        description:
            'One port per output format. Console output goes to the Terminal and, in drawing mode, to the Screen’s text cursor in 8 by 8 cells. In TRS-80 mode it goes only to the Terminal.'
    },
    {
        group: 'screen',
        title: 'Screen',
        range: '0x20 - 0x2A',
        description:
            'Drawing. Set the colors and the coordinates, then write one command to the command port; every coordinate is a byte, so the Screen is at most 256 by 256 pixels and is 256 by 192 until the program resizes it. Colors are one 3-3-2 byte.'
    },
    {
        group: 'keyboard',
        title: 'Keyboard',
        range: '0x30 - 0x33',
        description:
            'Input from the focused Screen, polled: whether a typed character is waiting (read it from the character port), whether a given key is held, and the last key pressed and released. Key codes are EASy68K’s, the same table every environment here uses: letters and digits are the ASCII code of their capital, 0x25 to 0x28 are the arrows left, up, right and down, 0x20 is the space bar and 0x0D is Enter. The Enter key code is distinct from the newline character 0x0A returned by CHAR.'
    },
    {
        group: 'mouse',
        title: 'Mouse',
        range: '0x40 - 0x43',
        description:
            'Pointing input over the Screen, polled, in the same logical pixels drawing uses. B selects which of the three views a read answers with: the current state, the state at the last button release, or the state at the last button press.'
    },
    {
        group: 'time',
        title: 'Program time',
        range: '0x50 - 0x52',
        description:
            'Delay the program, synchronize animation frames, or read elapsed time. Waits leave the editor responsive: Stop still works and the Screen can repaint.'
    },
    {
        group: 'audio',
        title: 'Audio',
        range: '0x60 - 0x64',
        description:
            'Play notes with a pitch, instrument sound, and volume, each numbered from 0 to 127, and a duration in milliseconds. General MIDI is the standard list of instrument sounds. Write a pitch to NOTE_PLAY (0x60) to start a note and continue immediately, allowing notes to overlap as a chord. Read NOTE_PLAY_WAIT (0x61) with the pitch in B to play a note and wait for its requested duration; a sequence of these reads can play a melody. Testcases are silent, and waits complete immediately while advancing the virtual clock.'
    },
    {
        group: 'sound-chip',
        title: 'Sound chip',
        range: '0xA0 - 0xA2',
        description:
            "The AY-3-8910 is the sound chip used in the MSX, ZX Spectrum 128 and Amstrad CPC. Here it uses the MSX register ports and 1.7897725 MHz clock, so compatible AY register programming uses the same port addresses and periods. It has three square-wave tone channels, A, B and C. Each channel can mix its tone with noise and use either a fixed volume or an envelope that changes the volume over time. All channels share one noise generator and one envelope.\n\nSelect a register through `0xA0`, then write it through `0xA1` or read it through `0xA2`. Two-byte periods use the lower-numbered register for the low byte.\n\n| Registers | Bits kept | Meaning |\n| --- | --- | --- |\n| 0/1, 2/3, 4/5 | 8 low + 4 high | Tone periods for A, B and C, 0 to 4095 |\n| 6 | 5 | Shared noise period, 0 to 31 |\n| 7 | 8 | Mixer: bits 0–2 disable the tones of A/B/C; bits 3–5 disable their noise. A 0 bit enables that source. Bits 6/7 are I/O-direction controls; they are retained on readback but have no effect here because the I/O ports are unconnected. |\n| 8, 9, 10 | 5 | Volumes for A, B and C: bit 4 clear uses bits 0–3 as fixed volume, 0 to 15; bit 4 set follows the shared envelope instead |\n| 11/12 | 8 low + 8 high | Shared envelope period, 0 to 65,535 |\n| 13 | 4 | Shared envelope shape, 0 to 15; every write restarts it for all channels following the envelope |\n| 14, 15 | none | Unconnected I/O ports: read `0xFF`, writes ignored |\n\nA tone's frequency is 1,789,772.5 ÷ (16 × period) Hz: period 254 is about 440 Hz, the A above middle C. The noise generator advances at the same clock ÷ (16 × noise period) rate; smaller periods give faster, brighter noise. Tone, noise and envelope periods of 0 act as 1, while still reading back as 0.\n\nOne complete envelope rise or fall takes 256 × envelope period ÷ 1,789,772.5 seconds. The shape selects what follows that first rise or fall:\n\n| Shape | First movement | Then |\n| --- | --- | --- |\n| 0–3 | Fall from full volume to 0 | Hold at 0 |\n| 4–7 | Rise from 0 to full volume | Drop to 0 and hold |\n| 8 | Fall | Repeat falling ramps |\n| 9 | Fall | Hold at 0 |\n| 10 | Fall | Alternate rising and falling ramps |\n| 11 | Fall | Jump to full volume and hold |\n| 12 | Rise | Repeat rising ramps |\n| 13 | Rise | Hold at full volume |\n| 14 | Rise | Alternate falling and rising ramps |\n| 15 | Rise | Drop to 0 and hold |\n\nChanging the noise period affects every channel with noise enabled. Writing register 13 retriggers every channel using the envelope, even if its shape value is unchanged.\n\n| Note | C | C♯ | D | D♯ | E | F | F♯ | G | G♯ | A | A♯ | B |\n| --- | --- | --- | --- | --- | --- | --- | --- | --- | --- | --- | --- | --- |\n| Period from middle C | 428 | 404 | 381 | 360 | 339 | 320 | 302 | 285 | 269 | 254 | 240 | 226 |\n\nAn octave lower doubles the tone period and an octave higher halves it. Every Build starts with every register 0, so every channel is silent. The chip keeps sounding while the program is paused or stopped at a breakpoint, and falls silent when the program ends. Testcases are silent and retain the same register readback."
    }
]

/**
 * The operations of the Screen command port. One write runs one operation on the coordinates,
 * colors and pen width already written to the other ports — the Screen's own drawing operations,
 * the same ones EASy68K's tasks reach
 * ([ADR 0003](../../../../../../docs/adr/0003-preserve-simulator-graphics-conventions.md)).
 */
export const Z80_SCREEN_COMMANDS = {
    PIXEL: 0,
    LINE: 1,
    LINE_TO: 2,
    MOVE_TO: 3,
    RECTANGLE: 4,
    RECTANGLE_OUTLINE: 5,
    ELLIPSE: 6,
    ELLIPSE_OUTLINE: 7,
    FLOOD_FILL: 8,
    CLEAR: 9,
    RESIZE: 10,
    BUFFERING_ON: 11,
    BUFFERING_OFF: 12,
    PRESENT: 13,
    MODE_CELLS: 14,
    MODE_DRAWING: 15
} as const

export type Z80ScreenCommandName = keyof typeof Z80_SCREEN_COMMANDS

//each row takes its number from Z80_SCREEN_COMMANDS by its name, so the two cannot disagree
const SCREEN_COMMAND_ROWS: { name: Z80ScreenCommandName; description: string }[] = [
    {
        name: 'PIXEL',
        description: 'One pixel at (X, Y) in the pen color. The pen width does not apply.'
    },
    {
        name: 'LINE',
        description:
            'A line from (X, Y) to (X2, Y2) in the pen color, leaving the drawing position at (X2, Y2).'
    },
    {
        name: 'LINE_TO',
        description:
            'A line from the drawing position to (X, Y), which becomes the new position: a polyline costs one command per point.'
    },
    {
        name: 'MOVE_TO',
        description: 'Moves the drawing position to (X, Y) without drawing.'
    },
    {
        name: 'RECTANGLE',
        description:
            'A rectangle from (X, Y) to (X2, Y2), filled with the fill color and outlined with the pen. The right and bottom edges are excluded, as they are in EASy68K.'
    },
    {
        name: 'RECTANGLE_OUTLINE',
        description: 'The same rectangle, outline only.'
    },
    {
        name: 'ELLIPSE',
        description: 'The ellipse inscribed in that rectangle, filled and outlined like it.'
    },
    {
        name: 'ELLIPSE_OUTLINE',
        description: 'The same ellipse, outline only.'
    },
    {
        name: 'FLOOD_FILL',
        description:
            'Spreads the fill color from (X, Y) over every pixel of the color that was there, four ways.'
    },
    {
        name: 'CLEAR',
        description:
            'Fills the whole Screen with the fill color, homes the text cursor, and adopts that color as the background, so scrolled text rows and a later resize match what is on screen.'
    },
    {
        name: 'RESIZE',
        description:
            'Resizes the Screen to X by Y pixels and clears it; a coordinate of 0 means 256, the largest size a byte cannot hold.'
    },
    {
        name: 'BUFFERING_ON',
        description:
            'Double buffering on: drawing goes to an off-screen copy of the visible image until PRESENT, so an animation never shows a half-drawn frame.'
    },
    {
        name: 'BUFFERING_OFF',
        description:
            'Double buffering off: drawing appears immediately. The off-screen image is dropped without being shown.'
    },
    {
        name: 'PRESENT',
        description:
            'Shows the off-screen image. With double buffering off it only asks for a repaint.'
    },
    {
        name: 'MODE_CELLS',
        description:
            'Switches to the [TRS-80 display](/documentation/z80/io#the-trs-80-display). Draw by storing bytes in video memory; see that section for the memory map, character encoding and keyboard matrix.'
    },
    {
        name: 'MODE_DRAWING',
        description:
            'Back to the drawing commands above. The image stays as it is until something draws on it, and memory at 0x3C00 becomes ordinary RAM again.'
    }
]

/** One row per command for the documentation page and the coding agent's prompt. */
export const Z80_SCREEN_COMMAND_DOCS: {
    name: Z80ScreenCommandName
    command: number
    description: string
}[] = SCREEN_COMMAND_ROWS.map((row) => ({ ...row, command: Z80_SCREEN_COMMANDS[row.name] }))

/**
 * Which snapshot a mouse read answers with, in B. The numbers are EASy68K's task 61 modes, so a
 * program (or a reader) moving between the two environments finds the same three views.
 */
export const Z80_MOUSE_VIEWS = {
    /** Where the pointer is now and which buttons are down now. */
    CURRENT: 0,
    /** The state at the last button release, which persists until the next one. */
    LAST_UP: 1,
    /** The state at the last button press, with the double-click flag. */
    LAST_DOWN: 2
} as const

/**
 * The bits of the mouse buttons port. The layout is EASy68K's task 61 flags byte — Ctrl, Alt,
 * Shift, Double, Middle, Right, Left written from the high bit down — so the two environments
 * report a click the same way.
 */
export const Z80_MOUSE_FLAGS = {
    LEFT: 0x01,
    RIGHT: 0x02,
    MIDDLE: 0x04,
    /** Only ever set in the last-press view: the press came within the double-click interval. */
    DOUBLE: 0x08,
    SHIFT: 0x10,
    ALT: 0x20,
    CTRL: 0x40
} as const

/**
 * Colors worth naming in the 3-3-2 layout a color byte uses: three bits of red in bits 7-5, three
 * of green in bits 4-2 and two of blue in bits 1-0. The adapter expands each field to eight bits by
 * repeating it, so 0xFF is pure white and 0xE0 pure red. The two grays are the nearest the two blue
 * bits allow, which is why they are not exactly neutral.
 */
export const Z80_COLORS = {
    BLACK: 0x00,
    BLUE: 0x03,
    GREEN: 0x1c,
    CYAN: 0x1f,
    RED: 0xe0,
    MAGENTA: 0xe3,
    YELLOW: 0xfc,
    WHITE: 0xff,
    ORANGE: 0xf0,
    GRAY: 0x92,
    DARK_GRAY: 0x49
} as const

/**
 * Human descriptions of every port, for the I/O documentation page, the hover provider and the
 * coding agent's prompt. Examples are runnable in the interactive editor: `exampleInput` is the
 * input the program consumes, `exampleOutput` the exact text it prints and `exampleShows` what it
 * leaves on the Screen, and a test assembles and runs every one of them.
 */
export type Z80PortDoc = {
    name: Z80PortName
    port: number
    group: Z80PortGroup
    title: string
    write: string
    read: string
    example?: string
    /** Lines the example's input reads consume, so its documented output is exact. */
    exampleInput?: string[]
    /** The exact text the example prints. */
    exampleOutput?: string
    /** What the example leaves on the Screen, or what it waits for, when that is the point of it. */
    exampleShows?: string
}

//each row takes its port from Z80_PORTS by its name, so the two cannot disagree
const PORT_ROWS: Omit<Z80PortDoc, 'port'>[] = [
    {
        name: 'CHAR',
        group: 'console',
        title: 'Character',
        write: 'Prints the byte as a Latin-1 character to the Terminal and, in drawing mode, at the Screen’s text cursor. 0x0A prints a newline. At the bottom, text scrolls the whole Screen up one cell row, graphics included. In TRS-80 mode output goes only to the Terminal.',
        read: 'Reads a Terminal line one character at a time, pausing when no input is buffered. Pressing Enter ends the line with newline 0x0A. Using a Screen, Keyboard or Mouse port in drawing mode, other than a display-mode command, switches character input to the Screen for the rest of the run. Click the Screen to focus it before typing. Each read then takes one keystroke; Enter still returns 0x0A. Typed characters are echoed at the text cursor while in drawing mode.',
        example: `        .org 0x8000
        ld hl, msg
loop:   ld a, (hl)
        or a
        jr z, done
        out (0x10), a
        inc hl
        jr loop
done:   halt
msg:    .asciz "Hello!", 10   ; 10 is the newline: strings keep \\n literally`,
        exampleOutput: 'Hello!\n'
    },
    {
        name: 'NUMBER',
        group: 'console',
        title: 'Unsigned number',
        write: 'Prints the byte as an unsigned decimal number, 0 to 255.',
        read: 'Reads a line, parses it as a decimal number and returns its low byte. Stops the program with an error when the line is not a number.',
        example: `        .org 0x8000
        in a, (0x11)       ; ask for a number
        add a, a        ; double it
        out (0x11), a      ; print it
        halt`,
        exampleInput: ['21'],
        exampleOutput: '42'
    },
    {
        name: 'SIGNED',
        group: 'console',
        title: 'Signed number',
        write: 'Prints the byte as a signed decimal number, -128 to 127.',
        read: 'Same as the unsigned number port.',
        example: `        .org 0x8000
        ld a, 5
        sub 10
        out (0x12), a
        halt`,
        exampleOutput: '-5'
    },
    {
        name: 'HEX',
        group: 'console',
        title: 'Hexadecimal',
        write: 'Prints the byte as two upper case hexadecimal digits.',
        read: 'Reads a line, parses it as a hexadecimal number (`0x`, `$` prefix or `h` suffix accepted) and returns its low byte.',
        example: `        .org 0x8000
        ld a, 255
        out (0x13), a
        halt`,
        exampleOutput: 'FF'
    },
    {
        name: 'WORD',
        group: 'console',
        title: '16 bit number',
        write: 'Prints the 16 bit number made of the high byte of the port address (register B when using `out (c),r`) and the byte written, as an unsigned decimal number.',
        read: 'Same as the unsigned number port.',
        example: `        .org 0x8000
        ld hl, 1000
        ld b, h         ; high byte goes on the address bus
        ld c, 0x14      ; port number
        out (c), l      ; prints HL
        halt`,
        exampleOutput: '1000'
    },
    {
        name: 'SCREEN_PEN_COLOR',
        group: 'screen',
        title: 'Pen color',
        write: 'Sets the color of pixels, lines, outlines and text, as one 3-3-2 byte (`RRRGGGBB`).',
        read: 'The current pen color, back in 3-3-2.',
        example: `        .org 0x8000
        ld a, 0xE0      ; 111 000 00: pure red
        out (0x20), a   ; pen color
        ld a, 100
        out (0x23), a   ; X
        ld a, 50
        out (0x24), a   ; Y
        xor a           ; command 0: pixel
        out (0x27), a
        halt`,
        exampleShows: 'one red pixel at (100, 50).'
    },
    {
        name: 'SCREEN_FILL_COLOR',
        group: 'screen',
        title: 'Fill color',
        write: 'Sets the color the inside of a filled shape, a flood fill and a clear use, as one 3-3-2 byte.',
        read: 'The current fill color, back in 3-3-2.',
        example: `        .org 0x8000
        ld a, 0x1C      ; 000 111 00: pure green
        out (0x21), a   ; fill color
        ld a, 0xFF
        out (0x20), a   ; white pen for the outline
        ld a, 20
        out (0x23), a
        out (0x24), a   ; from (20, 20)
        ld a, 80
        out (0x25), a
        out (0x26), a   ; to (80, 80), right and bottom excluded
        ld a, 4         ; command 4: filled rectangle
        out (0x27), a
        halt`,
        exampleShows: 'a green square with a white outline, 60 by 60 pixels, at (20, 20).'
    },
    {
        name: 'SCREEN_PEN_WIDTH',
        group: 'screen',
        title: 'Pen width',
        write: 'Sets how many pixels wide lines and outlines are, at least 1. A width of 0 is read as 1.',
        read: 'The current pen width.',
        example: `        .org 0x8000
        ld a, 5
        out (0x22), a   ; five pixels wide
        ld a, 10
        out (0x23), a
        out (0x24), a   ; from (10, 10)
        ld a, 200
        out (0x25), a
        ld a, 150
        out (0x26), a   ; to (200, 150)
        ld a, 1         ; command 1: line
        out (0x27), a
        halt`,
        exampleShows: 'a thick white diagonal line across the Screen.'
    },
    {
        name: 'SCREEN_X',
        group: 'screen',
        title: 'X',
        write: 'The first X coordinate: the pixel, the start of a line, the left edge of a shape, the width of a resize.',
        read: 'The byte last written.',
        example: `        .org 0x8000
        ld a, 128
        out (0x23), a
        ld a, 96
        out (0x24), a   ; (128, 96), the middle of the default Screen
        ld a, 3         ; command 3: move to
        out (0x27), a
        ld a, 200
        out (0x23), a
        ld a, 20
        out (0x24), a
        ld a, 2         ; command 2: line to
        out (0x27), a
        halt`,
        exampleShows: 'a line from the middle of the Screen to (200, 20).'
    },
    {
        name: 'SCREEN_Y',
        group: 'screen',
        title: 'Y',
        write: 'The first Y coordinate, the companion of the X port, and the height of a resize.',
        read: 'The byte last written.'
    },
    {
        name: 'SCREEN_X2',
        group: 'screen',
        title: 'X2',
        write: 'The second X coordinate: the end of a line, the right edge of a shape. Unused by the one-point commands.',
        read: 'The byte last written.'
    },
    {
        name: 'SCREEN_Y2',
        group: 'screen',
        title: 'Y2',
        write: 'The second Y coordinate, the companion of the X2 port.',
        read: 'The byte last written.'
    },
    {
        name: 'SCREEN_COMMAND',
        group: 'screen',
        title: 'Command',
        write: 'Runs one drawing operation on the coordinates, colors and pen width already set. The commands are listed above.',
        read: 'The number of the last command run, 0 before the first one.',
        example: `        .org 0x8000
        ld a, 0x03      ; 000 000 11: pure blue
        out (0x21), a   ; fill color
        ld a, 9         ; command 9: clear to the fill color
        out (0x27), a
        ld a, 0xFC      ; 111 111 00: yellow
        out (0x21), a
        ld a, 60
        out (0x23), a
        ld a, 40
        out (0x24), a
        ld a, 196
        out (0x25), a
        ld a, 152
        out (0x26), a
        ld a, 6         ; command 6: filled ellipse
        out (0x27), a
        halt`,
        exampleShows: 'a yellow ellipse with a white outline on a blue Screen.'
    },
    {
        name: 'SCREEN_PIXEL',
        group: 'screen',
        title: 'Pixel color',
        write: 'Ignored: the pixel is drawn with command 0, in the pen color.',
        read: 'The color of the pixel at (X, Y) as a 3-3-2 byte, read from the image being drawn on — the off-screen one while double buffering. Outside the Screen it reads as the background color.',
        example: `        .org 0x8000
        ld a, 0xE0
        out (0x20), a   ; red pen
        ld a, 8
        out (0x23), a
        out (0x24), a   ; (8, 8)
        xor a           ; command 0: pixel
        out (0x27), a
        in a, (0x28)    ; read the color back
        out (0x13), a      ; print it as hexadecimal
        halt`,
        exampleOutput: 'E0'
    },
    {
        name: 'SCREEN_CURSOR_COLUMN',
        group: 'screen',
        title: 'Text cursor column',
        write: 'Moves the text cursor to a column, in 8 by 8 character cells: 0 to 31 on the default Screen. Out of range values are clamped.',
        read: 'The column the text cursor is on.',
        example: `        .org 0x8000
        ld a, 12
        out (0x29), a   ; column 12
        ld a, 8
        out (0x2A), a   ; row 8
        ld hl, msg
loop:   ld a, (hl)
        or a
        jr z, done
        out (0x10), a      ; the character port draws at the cursor
        inc hl
        jr loop
done:   halt
msg:    .asciz "HELLO"`,
        exampleOutput: 'HELLO',
        exampleShows: 'the same text on the Screen, at the eighth row.'
    },
    {
        name: 'SCREEN_CURSOR_ROW',
        group: 'screen',
        title: 'Text cursor row',
        write: 'Moves the text cursor to a row, in 8 by 8 character cells: 0 to 23 on the default Screen. Out of range values are clamped.',
        read: 'The row the text cursor is on.'
    },
    {
        name: 'KEY_AVAILABLE',
        group: 'keyboard',
        title: 'Typed input available',
        write: 'Ignored.',
        read: '1 when a character typed on the focused Screen is waiting to be read from the character port, else 0. During a testcase it answers for the scripted input instead, so a program that polls before reading works in both.',
        example: `        .org 0x8000
wait:   in a, (0x30)    ; anything typed?
        or a
        jr z, wait      ; poll until there is
        in a, (0x10)    ; take the character
        out (0x10), a   ; print it
        halt`,
        exampleInput: ['hi'],
        exampleShows:
            'the first character typed on the Screen, echoed back. Click the Screen first: that is where the keys go.'
    },
    {
        name: 'KEY_STATE',
        group: 'keyboard',
        title: 'Key state',
        write: 'Ignored.',
        read: '1 while the key whose code is in B is held down, else 0. This is how a game reads WASD or the arrows: it never consumes anything, and a key held over several reads answers 1 every time.',
        example: `        .org 0x8000
        ld c, 0x31      ; the key state port
        ld b, 0x27      ; the right arrow
wait:   in a, (c)
        or a
        jr z, wait      ; poll until it is held
        ld a, 'R'
        out (0x10), a
        halt`,
        exampleShows: 'an R printed once the right arrow is held down on the focused Screen.'
    },
    {
        name: 'KEY_LAST_DOWN',
        group: 'keyboard',
        title: 'Last key pressed',
        write: 'Ignored.',
        read: 'The code of the last key pressed on the Screen, 0 before the first press. It persists, so a program that polls slowly still sees the key.',
        example: `        .org 0x8000
loop:   in a, (0x32)    ; the last key pressed
        or a
        jr z, loop
        out (0x13), a      ; print its code in hexadecimal
        halt`,
        exampleShows: 'the code of the first key pressed on the focused Screen, in hexadecimal.'
    },
    {
        name: 'KEY_LAST_UP',
        group: 'keyboard',
        title: 'Last key released',
        write: 'Ignored.',
        read: 'The code of the last key released, 0 before the first release. With the last-pressed port it is the pair EASy68K’s task 19 answers with.'
    },
    {
        name: 'MOUSE_X',
        group: 'mouse',
        title: 'Mouse X',
        write: 'Ignored.',
        read: 'The X of the view selected by B (0 current, 1 last release, 2 last press), in logical Screen pixels from the left edge, independently of how the panel is zoomed.',
        example: `        .org 0x8000
        ld c, 0x42      ; the buttons port
        ld b, 0         ; view 0: the current state
wait:   in a, (c)
        and 1           ; the left button
        jr z, wait
        ld c, 0x40
        in a, (c)       ; X
        out (0x11), a
        ld a, ','
        out (0x10), a
        ld c, 0x41
        in a, (c)       ; Y
        out (0x11), a
        halt`,
        exampleShows:
            'the pointer position printed as "x,y" as soon as the left button is held over the Screen.'
    },
    {
        name: 'MOUSE_Y',
        group: 'mouse',
        title: 'Mouse Y',
        write: 'Ignored.',
        read: 'The Y of the view selected by B, in logical Screen pixels from the top edge.'
    },
    {
        name: 'MOUSE_BUTTONS',
        group: 'mouse',
        title: 'Mouse buttons',
        write: 'Ignored.',
        read: 'The buttons and modifiers of the view selected by B: bit 0 left, bit 1 right, bit 2 middle, bit 3 the double-click flag (only in the last-press view), bit 4 Shift, bit 5 Alt, bit 6 Ctrl.'
    },
    {
        name: 'MOUSE_EVENTS',
        group: 'mouse',
        title: 'Mouse event count',
        write: 'Ignored.',
        read: 'For the current view, how many mouse events have happened, as a byte that wraps around; for the two snapshot views, the count at the moment of the snapshot, 0 when it has not happened yet. Comparing it with the previous read is how a program tells a new click from the one it already handled.'
    },
    {
        name: 'TIME_WAIT',
        group: 'time',
        title: 'Wait',
        write: 'Ignored.',
        read: 'Waits B hundredths of a second, then answers 0. In a testcase the wait completes at once and advances the virtual clock by the requested duration.',
        example: `        .org 0x8000
        ld c, 0x50
        ld b, 25        ; a quarter of a second
        in a, (c)
        ld a, '!'
        out (0x10), a
        halt`,
        exampleOutput: '!'
    },
    {
        name: 'TIME_FRAME',
        group: 'time',
        title: 'Frame sync',
        write: 'Ignored.',
        read: 'Waits for the next animation frame, then answers 0. One read per frame is how an animation runs at the display’s own pace instead of as fast as the host can go; with double buffering, present the frame first and sync afterwards. In a testcase the wait completes at once and advances the virtual clock.',
        example: `        .org 0x8000
        ld b, 10        ; ten frames
loop:   push bc
        in a, (0x51)    ; wait for the next frame
        ld a, '.'
        out (0x10), a
        pop bc
        djnz loop
        halt`,
        exampleOutput: '..........'
    },
    {
        name: 'TIME_NOW',
        group: 'time',
        title: 'Elapsed time',
        write: 'Ignored.',
        read: 'One byte of the number of hundredths of a second since the run started, selected by B: 0 the lowest byte, 3 the highest. This is elapsed time, not a calendar clock. A testcase reads a virtual clock that starts at zero and only moves when the program waits, so a test is reproducible.',
        example: `        .org 0x8000
        ld c, 0x50
        ld b, 10        ; wait a tenth of a second
        in a, (c)
        ld c, 0x52
        ld b, 0         ; the lowest byte of the elapsed hundredths
        in a, (c)
        out (0x11), a
        halt`,
        exampleShows: 'the hundredths of a second the program has been running, about 10.'
    },
    {
        name: 'NOTE_PLAY',
        group: 'audio',
        title: 'Play a note',
        write: 'Writing a pitch starts a note and continues immediately, so notes can overlap. Pitch runs from 0 to 127: 60 is middle C, 62 is D, 64 is E, and each step is one semitone. The note uses the instrument, volume, and duration last set through the other audio ports. A pitch of 128 or more becomes 60, as in MARS service 31. The first note or MIDI-file request after opening or reloading the editor page waits while the browser loads the sound bank, the collection of instrument sounds; later runs share it.',
        read: 'Returns 0xFF; this port only accepts writes.',
        example: `        .org 0x8000
        ld a, 60        ; C
        out (0x60), a
        ld a, 64        ; E, sounding with it
        out (0x60), a
        ld a, 67        ; G: a C major chord
        out (0x60), a
        halt`,
        exampleShows:
            'No text output; plays a C major chord on the piano for one second, continuing after the program ends.'
    },
    {
        name: 'NOTE_PLAY_WAIT',
        group: 'audio',
        title: 'Play a note and wait',
        write: 'Ignored.',
        read: 'Read with `in r,(c)` and the pitch in B to play a note using the instrument, volume, and duration already set. Waits for the requested duration, then returns 0, like MARS service 33. A sequence of these reads can play a melody. Muting or stopping the audible note does not shorten the wait. Testcases are silent; the wait completes immediately and advances the virtual clock by the duration.',
        example: `        .org 0x8000
        ld b, 0
        ld a, 150       ; 150 ms per note
        ld c, 0x64
        out (c), a
        ld c, 0x61
        ld b, 60        ; C
        in a, (c)
        ld b, 64        ; then E
        in a, (c)
        ld b, 67        ; then G
        in a, (c)
        ld a, '!'
        out (0x10), a
        halt`,
        exampleOutput: '!'
    },
    {
        name: 'NOTE_INSTRUMENT',
        group: 'audio',
        title: 'Instrument',
        write: 'Selects the instrument sound for later notes from the General MIDI list, numbered from 0 to 127: 0 is acoustic grand piano, 40 violin, 56 trumpet, and 73 flute. Values of 128 or more select 0 (piano), as in MARS. Every run starts at 0 (piano).',
        read: 'Returns 0xFF; this port only accepts writes.',
        example: `        .org 0x8000
        ld a, 40        ; violin
        out (0x62), a
        ld a, 69        ; A above middle C
        out (0x60), a
        halt`,
        exampleShows: 'No text output; plays an A on the violin for one second.'
    },
    {
        name: 'NOTE_VOLUME',
        group: 'audio',
        title: 'Volume',
        write: 'Sets the volume for later notes, from 0 (silent) to 127 (loudest). Values of 128 or more become 100, the MARS default. Every run starts at 100.',
        read: 'Returns 0xFF; this port only accepts writes.',
        example: `        .org 0x8000
        ld a, 30        ; quietly
        out (0x63), a
        ld a, 60
        out (0x60), a
        halt`,
        exampleShows: 'No text output; plays a quiet middle C.'
    },
    {
        name: 'NOTE_DURATION',
        group: 'audio',
        title: 'Duration',
        write: 'Sets the duration for later notes in milliseconds. Use `out (c),r` with C = 0x64: B supplies the high byte and the written register supplies the low byte, giving a duration up to 65,535 milliseconds. Every run starts at 1000 milliseconds, the MARS default.',
        read: 'Returns 0xFF; this port only accepts writes.',
        example: `        .org 0x8000
        ld b, 0x07      ; 0x07D0 = 2000 ms
        ld a, 0xd0
        ld c, 0x64
        out (c), a
        ld a, 48        ; a low C for two seconds
        out (0x60), a
        halt`,
        exampleShows: 'No text output; plays a low C for two seconds.'
    },
    {
        name: 'SOUND_CHIP_SELECT',
        group: 'sound-chip',
        title: 'Select a Sound chip register',
        write: 'Selects the register, 0 to 15, that the next writes to SOUND_CHIP_WRITE (0xA1) and reads from SOUND_CHIP_READ (0xA2) reach; it stays selected until the next write here. Registers 14 and 15 are the chip’s two I/O ports, which the MSX wires to its joysticks: nothing is attached here, so they read 0xFF and writes to them are ignored. A number of 16 or more selects nothing, as on the chip.',
        read: 'Returns 0xFF; this port only accepts writes.',
        example: `        .org 0x8000
        ld a, 0         ; register 0: channel A's period, low byte
        out (0xa0), a
        ld a, 254       ; 254 is 440 Hz
        out (0xa1), a
        ld a, 1         ; register 1: the period's high four bits
        out (0xa0), a
        xor a
        out (0xa1), a
        ld a, 7         ; register 7: the mixer
        out (0xa0), a
        ld a, 0xbe      ; only channel A's tone on
        out (0xa1), a
        ld a, 8         ; register 8: channel A's volume
        out (0xa0), a
        ld a, 15        ; the loudest
        out (0xa1), a
        ld c, 0x50
        ld b, 100       ; let it sound for a second
        in a, (c)
        halt`,
        exampleShows:
            'No text output; plays a square wave at 440 Hz for a second, which stops when the program ends.'
    },
    {
        name: 'SOUND_CHIP_WRITE',
        group: 'sound-chip',
        title: 'Write a Sound chip register',
        write: 'Writes the selected register. Each keeps only its own bits: four of a tone period’s high register, five of the noise period and of a volume, four of the envelope shape. Writing the envelope shape, register 13, restarts the envelope, even with the shape it already has; this retriggers all channels following the shared envelope.',
        read: 'Returns 0xFF; this port only accepts writes.',
        example: `        .org 0x8000
        ld hl, chord    ; pairs of a register and its value
        ld b, 10        ; ten of them
next:   ld a, (hl)
        out (0xa0), a
        inc hl
        ld a, (hl)
        out (0xa1), a
        inc hl
        djnz next
        ld c, 0x50
        ld b, 200       ; two seconds
        in a, (c)
        halt
chord:  .byte 0, 0xac, 1, 1     ; A: period 428, middle C
        .byte 2, 0x53, 3, 1     ; B: period 339, E
        .byte 4, 0x1d, 5, 1     ; C: period 285, G
        .byte 7, 0xb8           ; the three tones on, no noise
        .byte 8, 12, 9, 12      ; volumes of A and B
        .byte 10, 12            ; and of C`,
        exampleShows: 'No text output; plays a C major chord on the three channels for two seconds.'
    },
    {
        name: 'SOUND_CHIP_READ',
        group: 'sound-chip',
        title: 'Read a Sound chip register',
        write: 'Ignored.',
        read: 'Returns the selected register as the chip holds it: what was last written, less the bits the register does not keep. Registers 14 and 15, and a number of 16 or more, read 0xFF.',
        example: `        .org 0x8000
        ld a, 1         ; channel A's period, high bits
        out (0xa0), a
        ld a, 0xff
        out (0xa1), a
        in a, (0xa2)    ; the register keeps four bits
        out (0x11), a
        halt`,
        exampleOutput: '15'
    }
]

export const Z80_PORT_DOCS: Z80PortDoc[] = PORT_ROWS.map((row) => ({
    ...row,
    port: Z80_PORTS[row.name]
}))

const PORT_GROUP_BY_NAME = new Map<Z80PortName, Z80PortGroup>(
    Z80_PORT_DOCS.map((doc) => [doc.name, doc.group])
)

/**
 * The peripheral a port belongs to. Derived from the documentation table so a port cannot be
 * decoded into a group the documentation does not describe; a test pins that every port has a row.
 */
export function z80PortGroupOf(name: Z80PortName): Z80PortGroup {
    return PORT_GROUP_BY_NAME.get(name) ?? 'console'
}
